import React, { useState } from "react";
import { ChevronDown, ChevronUp } from "lucide-react";
import CouponCardShell from "./CouponCardShell";
import CouponUsageTable from "./CouponUsageTable";
import { COLORS, percent, safeNumber, formatDateTime } from "./couponUi";

const INACTIVE_NOTES = {
  EXPIRED: "Expired",
  EXHAUSTED: "Exhausted",
  INACTIVE: "Not activated yet",
  CANCELLED: "Cancelled",
};

const statBox = {
  flex: 1,
  minWidth: 90,
  textAlign: "center",
  padding: "10px 8px",
  border: `1px solid ${COLORS.mediumGrey}`,
  borderRadius: 8,
  backgroundColor: COLORS.white,
};

function usageLine(row) {
  if (!row || typeof row !== "object") return "—";
  const member =
    (row.member && typeof row.member === "object" && row.member.name) ||
    row.member_reference ||
    "—";
  const when = formatDateTime(row.used_at);
  return `${when} · ${member}`;
}

export default function SharedCouponCard({ coupon, familyId }) {
  const [showHistory, setShowHistory] = useState(false);
  const c = coupon && typeof coupon === "object" ? coupon : {};
  const status = typeof c.status === "string" ? c.status : "";
  const isActive = status === "ACTIVE";
  const inactiveNote = INACTIVE_NOTES[status] || null;

  const total = safeNumber(c.total);
  const used = safeNumber(c.used);
  const remaining = safeNumber(c.remaining);
  const pct = percent(c.used, c.total);
  const usage = Array.isArray(c.usage) ? c.usage : [];

  return (
    <CouponCardShell
      title={c.name || c.type || "Coupon"}
      status={c.status}
      activationDate={c.activation_date}
      expiryDate={c.expiry_date}
    >
      <div style={{ opacity: isActive ? 1 : 0.65, transition: "opacity 0.15s" }}>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 12 }}>
          <div style={statBox}>
            <div style={{ fontSize: 11, color: COLORS.mediumText, textTransform: "uppercase", letterSpacing: 0.4 }}>
              Total
            </div>
            <div style={{ fontSize: 22, fontWeight: 700, color: COLORS.darkText }}>{total}</div>
          </div>
          <div style={statBox}>
            <div style={{ fontSize: 11, color: COLORS.mediumText, textTransform: "uppercase", letterSpacing: 0.4 }}>
              Used
            </div>
            <div style={{ fontSize: 22, fontWeight: 700, color: COLORS.aquaBlue }}>{used}</div>
          </div>
          <div style={statBox}>
            <div style={{ fontSize: 11, color: COLORS.mediumText, textTransform: "uppercase", letterSpacing: 0.4 }}>
              Remaining
            </div>
            <div style={{ fontSize: 22, fontWeight: 700, color: COLORS.darkText }}>{remaining}</div>
          </div>
        </div>

        <div
          role="progressbar"
          aria-valuenow={typeof c.used === "number" ? c.used : 0}
          aria-valuemin={0}
          aria-valuemax={typeof c.total === "number" ? c.total : 0}
          aria-label={`${c.name || c.type || "Coupon"} usage`}
          style={{
            height: 10,
            backgroundColor: COLORS.mediumGrey,
            borderRadius: 999,
            overflow: "hidden",
          }}
        >
          <div
            style={{
              width: `${pct}%`,
              height: "100%",
              backgroundColor: isActive ? COLORS.aquaBlue : COLORS.mediumText,
              borderRadius: 999,
            }}
          />
        </div>
        <p style={{ margin: "6px 0 0 0", fontSize: 12, color: COLORS.mediumText }}>
          {used} of {total} used ({pct}%)
        </p>

        {!isActive && inactiveNote ? (
          <p
            style={{
              margin: "8px 0 0 0",
              fontSize: 12,
              fontWeight: 600,
              color: COLORS.mediumText,
            }}
          >
            {inactiveNote}
          </p>
        ) : null}
      </div>

      {usage.length > 0 ? (
        <div style={{ marginTop: 14 }}>
          <div style={{ fontSize: 12, fontWeight: 600, color: COLORS.darkText, marginBottom: 6 }}>Recent usage</div>
          <ul style={{ listStyle: "none", margin: 0, padding: 0, display: "flex", flexDirection: "column", gap: 4 }}>
            {usage.slice(0, 3).map((row, i) => (
              <li key={row?.id != null ? row.id : i} style={{ fontSize: 12, color: COLORS.mediumText }}>
                {usageLine(row)}
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      <button
        type="button"
        onClick={() => setShowHistory((v) => !v)}
        aria-expanded={showHistory}
        style={{
          marginTop: 12,
          display: "flex",
          alignItems: "center",
          gap: 4,
          backgroundColor: "transparent",
          border: "none",
          padding: 0,
          cursor: "pointer",
          fontSize: 12,
          fontWeight: 600,
          color: COLORS.aquaBlue,
        }}
      >
        {showHistory ? <ChevronUp size={14} aria-hidden="true" /> : <ChevronDown size={14} aria-hidden="true" />}
        Usage history
      </button>

      {showHistory ? (
        <div style={{ marginTop: 8 }}>
          <CouponUsageTable rows={usage} familyId={familyId} couponType={c.type} />
        </div>
      ) : null}
    </CouponCardShell>
  );
}
